import { useApolloClient } from '@apollo/client'
import { useEffectReducer } from 'use-effect-reducer'
import { TAG, useTrackLSPAYError } from '../errorTracking'
import { useTrackLSPAYTiming } from '../timeTracking'
import { useShouldUseSimulator } from '../useShouldUseSimulator'
import { useVdFeatureManager } from '../../../../../ng-compat/hooks'
import { SANDBOX_FLAG } from '../../helpers/lspayConstants'
import { getRefundEffectsMap } from './effects'
import { refundStateReducer } from './reducer'
import { RefundReducerArgs, RefundState, UnrefRefundReason } from './types'

export const useRefundEffectReducer = (args: RefundReducerArgs) => {
  const {
    retailerPaymentType,
    originalRegisterSalePaymentIds,
    originalSalePaidByOtherPT,
    amount,
    transactionId,
  } = args

  const trackError = useTrackLSPAYError(TAG.refund, {
    transactionId,
    originalRegisterSalePaymentIds,
  })
  const trackTiming = useTrackLSPAYTiming({
    transactionId,
    paymentTypeId: retailerPaymentType.type_id,
    isRefund: amount.isNegative(),
  })
  const shouldUseSimulator = useShouldUseSimulator()
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)

  const apolloClient = useApolloClient()

  const effectReducer = useEffectReducer(
    refundStateReducer,
    // initial state
    exec => {
      // blind refund
      if (
        !originalRegisterSalePaymentIds ||
        originalRegisterSalePaymentIds.length < 1
      ) {
        const unrefReason = originalSalePaidByOtherPT
          ? UnrefRefundReason.DIFFERENT_PAYMENT_TYPES
          : UnrefRefundReason.BLIND_REFUND
        return {
          status: 'unreferencedPrompt',
          retailerPaymentType,
          amount,
          errorMessage: undefined,
          dismissible: true,
          unrefReason,
        } as RefundState
      }

      // refund multiple payments
      if (originalRegisterSalePaymentIds.length > 1) {
        return {
          status: 'unreferencedPrompt',
          retailerPaymentType,
          amount,
          errorMessage: undefined,
          dismissible: true,
          unrefReason: UnrefRefundReason.MULTIPLE_PAYMENTS,
        } as RefundState
      }

      // else perform card not present refund
      exec({
        type: 'cardNotPresentEffect',
      })
      return {
        status: 'refundingViaCardNotPresent',
        errorMessage: undefined,
        retailerPaymentType,
        retryable: false,
      } as RefundState
    },
    getRefundEffectsMap({
      ...args,
      trackError,
      trackTiming,
      isSimulated: shouldUseSimulator,
      apolloClient,
      isSandbox,
    })
  )

  return effectReducer
}
